/**
 * Evaluations API client — Story 7.1 (Epic 7 — ATS Domain Expansion).
 *
 * Thin fetch helper for the read endpoint delivered by
 * `apps/api/src/routes/evaluations.ts`. Includes cookies (better-auth session)
 * and accepts JSON; non-2xx responses surface as thrown `Error` instances so
 * TanStack Query routes them through the standard error-state pipeline.
 *
 * The `transcript` field is the durable FusedTranscript persisted in D1
 * (`evaluations.aligned_transcript`, ADR-012 §D5), or `null` when no transcript
 * exists yet.
 */
import type { FusedTranscript } from '@caliber/shared'
import type { BehavioralResult, PresignResponse } from '@caliber/shared/dtos'
import { API_BASE } from '@/lib/api'
import { readProblemMessage } from '@/lib/api/problem'

export type EvaluationType = 'practical' | 'interview'
export type EvaluationStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled'

// ============================================================
// List surface — GET /api/evaluations.
// Mirrors `EvaluationListItem` / `EvaluationListResponse` from
// `apps/api/src/routes/evaluations.ts`. The backend supports `?status`,
// `?type`, `?applicationId`, `?assessmentId`, `?page`, `?pageSize` (capped at
// 200) and orders by `createdAt` desc. There is NO candidate-name search
// server-side. Every caller asks for a slice that is already scoped: one
// application (its evaluations tab), one assessment (its detail page), or the
// most recent ones (the dashboard's activity feed).
// ============================================================

/**
 * One row of `GET /api/evaluations` — mirrors `EvaluationListItem`. Timestamps
 * arrive as ISO-8601 STRINGS over the wire (Hono `c.json` serialises the `Date`
 * columns), not `Date` objects — same convention as {@link EvaluationDetail}.
 */
export interface EvaluationListItem {
  id: string
  applicationId: string | null
  professionalId: string
  candidateName: string | null
  assessmentId: string
  assessmentName: string | null
  type: EvaluationType
  status: EvaluationStatus
  score: number | null
  /** ISO-8601 timestamp string serialised by Hono `c.json`. */
  createdAt: string
  /** ISO-8601 timestamp string, or null when not completed. */
  completedAt: string | null
}

/** Response envelope for `GET /api/evaluations` — mirrors `EvaluationListResponse`. */
export interface EvaluationListResponse {
  items: EvaluationListItem[]
  page: number
  pageSize: number
  total: number
}

export interface ListEvaluationsParams {
  /** Server filter — omitted when absent. */
  status?: EvaluationStatus
  /** Server filter — omitted when absent. */
  type?: EvaluationType
  /** Server filter — omitted when absent. */
  applicationId?: string
  /**
   * Server filter (Story 13.1 / AC-5) — list only evaluations of this
   * assessment (filters the DIRECT `evaluations.assessmentId` column). Omitted
   * when absent. Accepts a slug (practical) or UUID (interview) id.
   */
  assessmentId?: string
  /** 1-indexed (default 1). */
  page?: number
  /** Default 50 server-side; backend caps at 200. */
  pageSize?: number
  signal?: AbortSignal
}

/**
 * GET /api/evaluations — paginated list with candidate/assessment display
 * names. Throws on non-2xx so TanStack Query routes it through its error
 * pipeline. The callers that list one application or one assessment fetch a
 * single large page (`pageSize=200`, the backend cap) and show it whole; the
 * dashboard asks only for its few most recent rows.
 */
export async function listEvaluations(
  params: ListEvaluationsParams = {},
): Promise<EvaluationListResponse> {
  const search = new URLSearchParams()
  if (params.status) search.set('status', params.status)
  if (params.type) search.set('type', params.type)
  if (params.applicationId) search.set('applicationId', params.applicationId)
  if (params.assessmentId) search.set('assessmentId', params.assessmentId)
  search.set('page', String(params.page ?? 1))
  search.set('pageSize', String(params.pageSize ?? 50))

  const init: RequestInit = {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }
  if (params.signal) init.signal = params.signal

  const res = await fetch(`${API_BASE}/evaluations?${search.toString()}`, init)
  if (!res.ok) {
    throw new Error(`Failed to list evaluations (${res.status})`)
  }
  return (await res.json()) as EvaluationListResponse
}

/**
 * Purpose classes accepted by the presign endpoint (mirror PRESIGN_PURPOSES).
 *
 * `assessment-statement` é o intruso desta lista, e de propósito: não pertence a
 * uma avaliação (a chave vive sob o prefixo PERSISTENTE `assessments/<id>/`, fora
 * do lifecycle de 24h) e o seu chamador é a tela do modelo de avaliação, não a de
 * criar avaliação. Fica aqui na mesma porque o endpoint é UM só e o `uploadFile`
 * abaixo — com as mensagens de falha do PUT direto ao R2 que já custaram uma
 * sessão de depuração — é o que se quer reusar. Duplicar o par presign+PUT num
 * segundo módulo seria duplicar essas mensagens.
 */
export type PresignPurpose = 'video' | 'user-transcript' | 'repo-archive' | 'assessment-statement'

/**
 * One graded check within an evaluation — mirrors `EvaluationCheckItem` from
 * `apps/api/src/routes/evaluations.ts`. `checkNumber` is a STRING (the API
 * already returns the array sorted numerically by it).
 */
export interface EvaluationCheckItem {
  checkNumber: string
  description: string
  status: 'PASS' | 'FAIL' | 'PARTIAL' | 'NOT_PERFORMED'
  score: number | null
  weight: number
  evidence: string | null
  interviewQuestionId: string | null
}

/**
 * Per-topic aggregated score — mirrors `EvaluationTopicScore`. Computed on the
 * fly by the API (no persisted table); `maxScore` is always 10.
 */
export interface EvaluationTopicScore {
  topic: string
  score: number
  maxScore: 10
  questionCount: number
}

/**
 * Response shape of `GET /api/evaluations/:id` — mirrors the ENRICHED
 * `EvaluationDetailResponse` (Story 4.6 / Epic 4 read API). Timestamps arrive as
 * ISO-8601 strings (Hono `c.json`). `transcript` is the durable FusedTranscript
 * or null. `checks` / `topicScores` are always present arrays (possibly empty).
 */
export interface EvaluationDetail {
  id: string
  applicationId: string | null
  professionalId: string
  candidateName: string | null
  assessmentId: string
  assessmentName: string | null
  type: EvaluationType
  status: EvaluationStatus
  score: number | null
  llmProvider: string
  llmModel: string
  reportMdR2Key: string | null
  reportHtmlR2Key: string | null
  /**
   * Existe relatório finalizado? Vem DERIVADO da API (OR de três colunas):
   * `report_data` para a entrevista — que desde 2026-07-29 deixou de escrever as
   * chaves R2 — e `report_md_r2_key`/`report_html_r2_key` para a prova prática e
   * o legado. Ler as chaves aqui daria "sem relatório" em toda a entrevista.
   */
  reportReady: boolean
  workflowInstanceId: string | null
  errorMessage: string | null
  transcript: FusedTranscript | null
  checks: EvaluationCheckItem[]
  topicScores: EvaluationTopicScore[]
  /**
   * Leitura comportamental da entrevista. `null` quando a avaliação é prática,
   * quando o assessment não tem rubrica comportamental, ou quando o passo
   * falhou — é COMPLEMENTAR e a ausência é um estado normal.
   *
   * NÃO entra no `score` — e desde o Epic 17 também não traduz nada em
   * recomendação (o `recommendationImpact` morreu sem substituto, D2). São
   * quatro competências com nível, razão e citações; a UI é obrigada a dizê-lo,
   * senão a leitura ao lado do técnico lê-se como parte dele.
   *
   * ⚠️ O tipo promete a forma nova, mas a promessa não é garantia: quem
   * renderiza trata o campo como dado não confiável (`readBehavioral`, em
   * `@caliber/render-shared`). Um resultado gravado antes do Epic 17 chega aqui
   * como `null` — a API valida-o contra o `BehavioralResultSchema` —, mas o
   * `tsc` não é quem impede um `undefined.map()` se isso mudar.
   */
  behavioralResult: BehavioralResult | null
  /**
   * A instrução em texto livre com que ESTA avaliação foi reprocessada (Story
   * 20.1), ou `null` quando ela nasceu sem instrução — a esmagadora maioria.
   *
   * ⚠️ O tipo diz `string | null`, mas um Worker ANTERIOR à Story 20.2 não
   * devolve a chave de todo: quem a lê recebe `undefined` durante a janela do
   * deploy sequencial (ou do cache de 5 min do `index.html` do Pages). Por isso
   * o marcador que a mostra (`MarcaDoReprocessamento`) guarda por VERACIDADE, e
   * nunca por `!== null` — `undefined !== null` é verdadeiro.
   */
  reprocessInstruction: string | null
  /** ISO-8601 timestamp string serialised by Hono `c.json`. */
  createdAt: string
  startedAt: string | null
  completedAt: string | null
}

/**
 * GET /api/evaluations/:id — evaluation detail incl. the durable transcript.
 * Throws on non-2xx (404 included — the caller handles it as a query error).
 */
export async function getEvaluation(id: string, signal?: AbortSignal): Promise<EvaluationDetail> {
  const init: RequestInit = {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }
  if (signal) init.signal = signal

  const res = await fetch(`${API_BASE}/evaluations/${encodeURIComponent(id)}`, init)
  if (!res.ok) {
    throw new Error(`Failed to load evaluation (${res.status})`)
  }
  return (await res.json()) as EvaluationDetail
}

/**
 * Shape returned by `POST /api/evaluations/:id/retry` (200) — Story 6.7. The
 * handler echoes the evaluation id, the reset `status` ('queued'), and the new
 * grading `workflowInstanceId`.
 */
export interface RetriedEvaluation {
  id: string
  status: EvaluationStatus
  workflowInstanceId: string | null
}

/**
 * POST /api/evaluations/:id/retry — re-dispatch the grading workflow for a
 * FAILED evaluation from the start (Story 6.7 — visible retry). The backend
 * re-fires the SAME workflow the evaluation's `type` maps to, reading the
 * persisted inputs (repoUrl/assessmentId) off the row, and resets the status to
 * 'queued'. Throws on non-2xx (404 missing, 409 when the evaluation is not in a
 * failed state / a practical has no repo to re-grade) so TanStack Query's
 * mutation `onError` surfaces it.
 */
export async function retryEvaluation(id: string): Promise<RetriedEvaluation> {
  const res = await fetch(`${API_BASE}/evaluations/${encodeURIComponent(id)}/retry`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new Error(`Failed to retry evaluation (${res.status})`)
  }
  return (await res.json()) as RetriedEvaluation
}

/** Shape returned by `POST /api/evaluations/:id/cancel` (200) — Story 8.1. */
export interface CancelledEvaluation {
  id: string
  status: EvaluationStatus
}

/**
 * POST /api/evaluations/:id/cancel — cancel an in-flight (queued/running)
 * evaluation, best-effort (Story 8.1 — FR-EX-6). The backend marks the row
 * `cancelled` and best-effort terminates the running workflow. Throws on non-2xx
 * (404 missing, 409 when the evaluation is already terminal) so the mutation
 * `onError` surfaces it.
 */
export async function cancelEvaluation(id: string): Promise<CancelledEvaluation> {
  const res = await fetch(`${API_BASE}/evaluations/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new Error(`Failed to cancel evaluation (${res.status})`)
  }
  return (await res.json()) as CancelledEvaluation
}

/**
 * GET /api/evaluations/:id/report-md — fetch the persisted report.md as plain
 * text for the in-app viewer (Story 8.2 — FR-RP-6). Throws on non-2xx (404 when
 * the evaluation/report does not exist) so TanStack Query routes it through its
 * error pipeline; the caller distinguishes 404 (empty state) from other errors.
 */
export async function getEvaluationReportMd(id: string, signal?: AbortSignal): Promise<string> {
  const init: RequestInit = { credentials: 'include', headers: { Accept: 'text/plain' } }
  if (signal) init.signal = signal

  const res = await fetch(`${API_BASE}/evaluations/${encodeURIComponent(id)}/report-md`, init)
  if (!res.ok) {
    throw new Error(`Failed to load report (${res.status})`)
  }
  return await res.text()
}

/**
 * GET /api/evaluations/:id/report-html — o relatório em HTML.
 *
 * `internal` decide QUE PÚBLICO: `true` (o default) acrescenta as métricas de
 * execução — modelo do LLM, tempo, consumo — e é a vista da aba Relatório.
 * `false` devolve o que o CLIENTE vê, igual ao link público.
 *
 * ⚠️ Quem gera um artefacto que SAI da casa pede `internal: false`. O botão de
 * copiar para email nasceu sem isto e pôs o modelo e o custo da avaliação no
 * email do cliente.
 *
 * É o mesmo documento que o cliente abre no link público, pelo mesmo motor, mais
 * as métricas de execução. Devolve o HTML completo (com `<style>` embutido) para
 * ser mostrado num iframe isolado — ver `ReportTab`.
 *
 * Lança em não-2xx (404 quando a avaliação ainda não tem `report_data`) para o
 * TanStack Query o encaminhar pelo pipeline de erro; quem chama distingue o 404
 * (estado vazio) dos restantes.
 */
export async function getEvaluationReportHtml(
  id: string,
  options?: { internal?: boolean; signal?: AbortSignal },
): Promise<string> {
  const init: RequestInit = { credentials: 'include', headers: { Accept: 'text/html' } }
  if (options?.signal) init.signal = options.signal

  // Só se envia o parâmetro para PEDIR a vista pública; o default do servidor
  // é o interno e é o que a aba Relatório quer.
  const query = options?.internal === false ? '?internal=false' : ''
  const res = await fetch(
    `${API_BASE}/evaluations/${encodeURIComponent(id)}/report-html${query}`,
    init,
  )
  if (!res.ok) {
    throw new Error(`Failed to load report (${res.status})`)
  }
  return await res.text()
}

// ============================================================
// Writer surface — Story 4.5 (ATS-first create evaluation).
// The client supplies ONLY applicationId + type (+ optional LLM override and
// the R2 input keys). candidate/assessment are DERIVED server-side from the
// application and its opportunity — there are NO candidate/assessment pickers.
// ============================================================

const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' }

/** Body for `POST /api/evaluations` — mirrors `CreateEvaluationSchema`. */
export interface CreateEvaluationInput {
  applicationId: string
  type: EvaluationType
  /**
   * Optional provider override. Fechado a `'openai'` na Story 22.1, a espelhar
   * o `CreateEvaluationSchema`: qualquer outro valor leva 400 do servidor.
   * Nenhuma tela o envia hoje — fica pelo espelho do contrato.
   */
  llmProvider?: 'openai'
  /**
   * Practical input — the git URL the runner clones, builds and tests (Story
   * 6.0-web). MANDATORY server-side when `type === 'practical'` (the handler
   * returns 422 without it); never sent for `type === 'interview'`. Mirrors
   * `CreateEvaluationSchema.repoUrl` (`z.string().url()`).
   */
  repoUrl?: string
  videoR2Key?: string
  userTranscriptR2Key?: string
}

/**
 * Shape returned by `POST /api/evaluations` (201). The handler echoes the
 * inserted row plus the dispatched `workflowInstanceId`. We only type the
 * fields the create flow consumes (`id` for the redirect); the rest are
 * tolerated via the index signature.
 */
export interface CreatedEvaluation {
  id: string
  status: EvaluationStatus
  applicationId: string
  professionalId: string
  assessmentId: string
  type: EvaluationType
  workflowInstanceId: string | null
}

/**
 * POST /api/evaluations — create an evaluation for an Application and dispatch
 * the grading workflow. Throws on non-2xx (400 invalid, 404 missing
 * application, 422 no assessment for type / no R2 input) so TanStack Query's
 * mutation `onError` surfaces it.
 */
export async function createEvaluation(body: CreateEvaluationInput): Promise<CreatedEvaluation> {
  const res = await fetch(`${API_BASE}/evaluations`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    throw new Error(`Failed to create evaluation (${res.status})`)
  }
  return (await res.json()) as CreatedEvaluation
}

/**
 * POST /api/evaluations/:id/reprocess (Task 2) — re-judges a COMPLETED
 * evaluation by creating a NEW one from the transcript (interview) or repo
 * URL (practical) already persisted, graded with the CURRENT config. Returns
 * the new evaluation in the same shape as `POST /api/evaluations` — hence
 * `CreatedEvaluation`, not the enriched `EvaluationDetail` (checks/topicScores
 * don't exist yet for a row that hasn't been graded). The caller navigates to
 * `/evaluations/${result.id}`.
 *
 * Throws on non-2xx with the server's `detail` (404 evaluation missing; 409
 * when it isn't completed, has no saved transcript, or is a practical with no
 * repo). The server writes that sentence to NAME THE WAY OUT ("crie uma
 * avaliação nova e reenvie…", "não tem repositório para re-corrigir") — it is
 * read as-is via {@link readProblemMessage}, never swapped for a generic
 * message, because a generic message would throw away the one thing the
 * sentence exists to say.
 *
 * `instruction` (Story 20.1) é OPCIONAL e dirige a nova correção em texto
 * livre. Vazia ou só espaços é o mesmo que ausente — e "ausente" aqui quer
 * dizer um pedido byte a byte igual ao de antes desta story, porque é isso que
 * os dois call sites simples continuam a fazer.
 */
export async function reprocessEvaluation(
  id: string,
  instruction?: string,
): Promise<CreatedEvaluation> {
  // ⚠️ O CAMINHO SEM INSTRUÇÃO TEM DE SAIR EXACTAMENTE COMO SAÍA. A rota
  // decide pelo TEXTO do corpo (vazio → `{}`, nunca 400), e mandar um
  // `Content-Type: application/json` com corpo nenhum é prometer um JSON que
  // não vai — por isso o cabeçalho e o `body` entram JUNTOS, ou nenhum entra.
  const texto = instruction?.trim() ?? ''
  const res = await fetch(`${API_BASE}/evaluations/${encodeURIComponent(id)}/reprocess`, {
    method: 'POST',
    credentials: 'include',
    headers: texto
      ? { Accept: 'application/json', 'Content-Type': 'application/json' }
      : { Accept: 'application/json' },
    ...(texto ? { body: JSON.stringify({ instruction: texto }) } : {}),
  })
  if (!res.ok) {
    throw new Error(await readProblemMessage(res, `Failed to reprocess evaluation (${res.status})`))
  }
  return (await res.json()) as CreatedEvaluation
}

/**
 * POST /api/uploads/presign — request a short-lived R2 PUT URL for a
 * browser-direct upload. `evaluationId` is optional (the server mints one when
 * absent); pass it to co-locate video + transcript under the same eval prefix.
 * `assessmentId` é o equivalente do outro prefixo: OBRIGATÓRIO quando
 * `purpose === 'assessment-statement'` (o servidor devolve 400 sem ele, porque é
 * o que constrói `assessments/<id>/statement/…`), ignorado nos demais.
 * Throws on non-2xx (413 oversize, 429 rate-limited).
 */
export async function presignUpload(body: {
  contentType: string
  sizeBytes: number
  purpose: PresignPurpose
  evaluationId?: string
  assessmentId?: string
}): Promise<PresignResponse> {
  const res = await fetch(`${API_BASE}/uploads/presign`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    throw new Error(`Failed to presign upload (${res.status})`)
  }
  return (await res.json()) as PresignResponse
}

/**
 * PUT the file straight to the presigned R2 URL with XHR (so we get upload
 * progress). Resolves on 2xx, rejects otherwise. `onProgress` receives 0..1.
 *
 * THE FAILURE MESSAGES ARE DELIBERATELY SPECIFIC. This PUT goes to R2 directly,
 * NOT through the worker — when it fails there is nothing in the API logs and
 * this rejection is the only witness. A generic message here cost a real
 * debugging session: the bucket had NO CORS rules, so every browser PUT died in
 * the preflight while the server logs showed a healthy
 * `POST /api/uploads/presign → 200` followed by silence, and the UI said only
 * "não foi possível criar a avaliação". `xhr.onerror` fires WITHOUT a status for
 * exactly two causes — the network dropped, or the browser refused the response
 * (CORS) — so the message names both instead of hiding them behind "network
 * error".
 *
 * `extraHeaders` (Story 25.2) — headers que o URL ASSINOU além do `content-type`,
 * e que por isso o PUT tem de mandar com o mesmo valor (senão o R2 dá 403). Hoje só
 * os anexos dos apontamentos o passam (`If-None-Match: *`, a escrita única). Omitido
 * → o PUT de sempre, só com `Content-Type`: o vídeo e a transcrição não mudam. O
 * `Content-Length` nunca entra aqui — o browser o põe a partir do `file`, e o JS
 * não o pode definir. Cada header novo tem de estar no CORS do bucket
 * (`apps/api/r2-cors.json`), senão o preflight o recusa.
 */
export function putToPresignedUrl(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
  extraHeaders?: Readonly<Record<string, string>>,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', uploadUrl)
    xhr.setRequestHeader('Content-Type', contentType)
    for (const [nome, valor] of Object.entries(extraHeaders ?? {})) {
      xhr.setRequestHeader(nome, valor)
    }

    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) onProgress(e.loaded / e.total)
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1)
        resolve()
      } else {
        reject(new Error(`o armazenamento recusou o arquivo (HTTP ${xhr.status})`))
      }
    }
    xhr.onerror = () =>
      reject(
        new Error(
          'o navegador não conseguiu enviar ao armazenamento (rede indisponível, ' +
            'ou o CORS do bucket R2 não permite esta origem)',
        ),
      )
    xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'))

    if (signal) {
      if (signal.aborted) {
        xhr.abort()
        return
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true })
    }

    xhr.send(file)
  })
}

/**
 * Presign + PUT in one call — returns the durable `r2Key` to attach to the
 * evaluation. Convenience used by the create flow.
 *
 * `opts.contentType` OVERRIDES `file.type` for both the presign and the PUT (the
 * signature binds `content-type`, so the two MUST be the same string). It exists
 * because `file.type` is whatever the OS guessed from the extension, while the
 * API accepts a CLOSED enum (`PRESIGN_CONTENT_TYPES`): a `.md` picked on macOS
 * arrives as `''` or `text/markdown` depending on the machine, and an empty type
 * fails the presign with a 400 that reads like a server bug. The caller resolves
 * the type from the extension and passes it here. Omitted → `file.type`, exactly
 * as before.
 */
export async function uploadFile(
  file: File,
  purpose: PresignPurpose,
  opts: {
    evaluationId?: string
    /** Obrigatório quando `purpose === 'assessment-statement'` (ver `presignUpload`). */
    assessmentId?: string
    contentType?: string
    onProgress?: (fraction: number) => void
    signal?: AbortSignal
  } = {},
): Promise<string> {
  const contentType = opts.contentType ?? file.type
  const presign = await presignUpload({
    contentType,
    sizeBytes: file.size,
    purpose,
    ...(opts.evaluationId && { evaluationId: opts.evaluationId }),
    ...(opts.assessmentId && { assessmentId: opts.assessmentId }),
  })
  await putToPresignedUrl(presign.uploadUrl, file, contentType, opts.onProgress, opts.signal)
  return presign.r2Key
}

// ============================================================
// Public share surface — Story 4.7 (Epic 4 — public sharing toggle).
// Three endpoints on `GET|POST|DELETE /api/evaluations/:id/share`. POST enables
// (mints an opaque token + absolute URL); DELETE disables (keeps the token for
// history); GET returns the current state. All three return the SAME shape so
// the UI can render uniformly off a single `ShareState`.
// ============================================================

/**
 * Public-share state for one evaluation — mirrors the response of all three
 * `/api/evaluations/:id/share` endpoints.
 *   • `enabled`      — whether the public link is currently active.
 *   • `token`        — the opaque 256-bit URL-safe token, or null when never shared.
 *   • `url`          — the ABSOLUTE share URL (`https://<host>/share/<token>`),
 *                      or null when not enabled. The server builds it from the
 *                      request host so the client never has to guess the origin.
 *   • `viewCount`    — number of times the public page has been viewed.
 *   • `lastViewedAt` — ISO-8601 timestamp of the most recent view, or null.
 */
export interface ShareState {
  enabled: boolean
  token: string | null
  url: string | null
  viewCount: number
  lastViewedAt: string | null
}

/**
 * GET /api/evaluations/:id/share — current public-share state.
 * Throws on non-2xx so TanStack Query routes it through its error pipeline.
 */
export async function getShareState(id: string, signal?: AbortSignal): Promise<ShareState> {
  const init: RequestInit = {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }
  if (signal) init.signal = signal

  const res = await fetch(`${API_BASE}/evaluations/${encodeURIComponent(id)}/share`, init)
  if (!res.ok) {
    throw new Error(`Failed to load share state (${res.status})`)
  }
  return (await res.json()) as ShareState
}

/**
 * POST /api/evaluations/:id/share — enable public sharing (mint token + URL).
 * Returns the enabled-state `ShareState`. Throws on non-2xx.
 */
export async function enableShare(id: string): Promise<ShareState> {
  const res = await fetch(`${API_BASE}/evaluations/${encodeURIComponent(id)}/share`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
  })
  if (!res.ok) {
    throw new Error(`Failed to enable share (${res.status})`)
  }
  return (await res.json()) as ShareState
}

/**
 * DELETE /api/evaluations/:id/share — disable public sharing (keeps the token
 * for history). Returns the disabled-state `ShareState`. Throws on non-2xx.
 */
export async function disableShare(id: string): Promise<ShareState> {
  const res = await fetch(`${API_BASE}/evaluations/${encodeURIComponent(id)}/share`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new Error(`Failed to disable share (${res.status})`)
  }
  return (await res.json()) as ShareState
}
